import React, { useState, useImperativeHandle, useEffect, useMemo } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { getActiveColorStyle } from "utils/colorUtils";
import {
  getTheme2FormFieldsCss,
  getTheme2SearchFilterSingleSelectStyles,
} from "utils/theme2MultiSelect";
import { THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } from "utils/sidebarShellTheme";
import {
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  Card,
  CardHeader,
  CardBody,
  CardTitle,
} from "reactstrap";
import { useGetChannels } from "api/channels/service";
import { useUpdateDeal } from "api/deals/service";
import Select from "components/Select";

const DealWholesalerTab = React.forwardRef(
  ({ data, id, activeColor, isLoading, isTheme2 = false }, ref) => {
  const { t } = useTranslation();
  const updateDealMutation = useUpdateDeal();
  const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
  const selectStyles = useMemo(
    () => getTheme2SearchFilterSingleSelectStyles(activeColorStyle, isTheme2),
    [activeColorStyle, isTheme2]
  );
  const theme2FieldsCss = useMemo(
    () =>
      isTheme2
        ? getTheme2FormFieldsCss({
            scopeSelector: ".deal-wholesaler-tab.deal-wholesaler-tab--theme2",
            fieldsSelector: ".deal-wholesaler-tab-fields",
          })
        : "",
    [isTheme2]
  );
  const inputStyle = isTheme2 ? { fontSize: THEME2_SIDEBAR_NAV_FONT_SIZE_VAR } : undefined;

  const { data: wholesalerData } = useGetChannels("dealer_wholesaler_auction");

  const [formData, setFormData] = useState({
    wholesaler_id: data?.wholesaler?.id || "",
    name: "",
    fed_id: "",
    legal_name: "",
    address_1: "",
    address_2: "",
    city: "",
    state: "",
    zip: "",
    county: "",
    contact_person: "",
    phone_1: "",
    phone_2: "",
    fax_1: "",
    fax_2: "",
    website: "",
    email: "",
  });

  // Update form data when data prop changes
  useEffect(() => {
    if (data?.wholesaler) {
      setFormData({
        wholesaler_id: data.wholesaler.id || "",
        name: data.wholesaler.name || "",
        fed_id: data.wholesaler.fed_id || "",
        legal_name: data.wholesaler.legal_name || "",
        address_1: data.wholesaler.address_1 || "",
        address_2: data.wholesaler.address_2 || "",
        city: data.wholesaler.city || "",
        state: typeof data.wholesaler.state === 'object' && data.wholesaler.state !== null
          ? (data.wholesaler.state.abbreviation || data.wholesaler.state.name || "")
          : (data.wholesaler.state || ""),
        zip: data.wholesaler.zip || "",
        county: data.wholesaler.county || "",
        contact_person: data.wholesaler.contact_person || "",
        phone_1: data.wholesaler.phone_1 || "",
        phone_2: data.wholesaler.phone_2 || "",
        fax_1: data.wholesaler.fax_1 || "",
        fax_2: data.wholesaler.fax_2 || "",
        website: data.wholesaler.website || "",
        email: data.wholesaler.email || "",
      });
    }
  }, [data]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleWholesalerSelect = (option) => {
    if (option) {
      const wholesaler = wholesalerData?.results?.find(w => w.id === option.value);
      if (wholesaler) {
        setFormData(prev => ({
          ...prev,
          wholesaler_id: wholesaler.id,
          name: wholesaler.name || "",
          fed_id: wholesaler.fed_id || "",
          legal_name: wholesaler.legal_name || "",
          address_1: wholesaler.address_1 || "",
          address_2: wholesaler.address_2 || "",
          city: wholesaler.city || "",
          state: typeof wholesaler.state === 'object' && wholesaler.state !== null
            ? (wholesaler.state.abbreviation || wholesaler.state.name || "")
            : (wholesaler.state || ""),
          zip: wholesaler.zip || "",
          county: wholesaler.county || "",
          contact_person: wholesaler.contact_person || "",
          phone_1: wholesaler.phone_1 || "",
          phone_2: wholesaler.phone_2 || "",
          fax_1: wholesaler.fax_1 || "",
          fax_2: wholesaler.fax_2 || "",
          website: wholesaler.website || "",
          email: wholesaler.email || "",
        }));
      }
    } else {
      // Clear form when no wholesaler selected
      setFormData(prev => ({
        ...prev,
        wholesaler_id: "",
        name: "",
        fed_id: "",
        legal_name: "",
        address_1: "",
        address_2: "",
        city: "",
        state: "",
        zip: "",
        county: "",
        contact_person: "",
        phone_1: "",
        phone_2: "",
        fax_1: "",
        fax_2: "",
        website: "",
        email: "",
      }));
    }
  };

  // Expose handleSave method to parent component
  useImperativeHandle(ref, () => ({
    handleSave: async () => {
      try {
        await updateDealMutation.mutateAsync({
          id: id,
          data: {
            wholesaler: formData.wholesaler_id,
          }
        });
      } catch (error) {
        console.error("Error updating wholesaler:", error);
        return { success: false, error };
      }
    }
  }));

  if (isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "200px" }}>
        <div className="spinner-border" role="status">
          <span className="sr-only">{t('dealWholesalerTab.loading')}</span>
        </div>
        <span className="ml-2">{t('dealWholesalerTab.loadingWholesalerData')}</span>
      </div>
    );
  }

  return (
    <div
      className={classNames("content deal-wholesaler-tab", {
        "deal-wholesaler-tab--theme2": isTheme2,
      })}
    >
      {isTheme2 ? <style>{theme2FieldsCss}</style> : null}
      <Row className={classNames({ "deal-wholesaler-tab-fields": isTheme2 })}>
        <Col md="12">
          <Card>
            <CardBody>
              {/* Wholesaler Selection */}
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label for="wholesaler_select">{t('dealWholesalerTab.selectWholesaler')}</Label>
                    <Select
                      activeColor={activeColor}
                      styles={selectStyles}
                      className="react-select"
                      placeholder={t('dealWholesalerTab.selectWholesaler')}
                      name="wholesaler_select"
                      isSearchable
                      closeMenuOnSelect={true}
                      value={formData.wholesaler_id ? {
                        value: formData.wholesaler_id,
                        label: formData.name || wholesalerData?.results?.find(w => w.id === formData.wholesaler_id)?.name || ""
                      } : null}
                      onChange={handleWholesalerSelect}
                      options={wholesalerData?.results?.map(wholesaler => ({
                        value: wholesaler.id,
                        label: wholesaler.name
                      })) || []}
                      isClearable={false}
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Wholesaler Information */}
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="name">{t('dealWholesalerTab.name')}</Label>
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterName')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="fed_id">{t('dealWholesalerTab.fedId')}</Label>
                    <Input
                      type="text"
                      name="fed_id"
                      id="fed_id"
                      value={formData.fed_id}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterFedId')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="legal_name">{t('dealWholesalerTab.legalName')}</Label>
                    <Input
                      type="text"
                      name="legal_name"
                      id="legal_name"
                      value={formData.legal_name}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterLegalName')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="address_1">{t('dealWholesalerTab.address1')}</Label>
                    <Input
                      type="text"
                      name="address_1"
                      id="address_1"
                      value={formData.address_1}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterAddress1')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="address_2">{t('dealWholesalerTab.address2')}</Label>
                    <Input
                      type="text"
                      name="address_2"
                      id="address_2"
                      value={formData.address_2}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterAddress2')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="city">{t('dealWholesalerTab.city')}</Label>
                    <Input
                      type="text"
                      name="city"
                      id="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterCity')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="4">
                  <FormGroup>
                    <Label for="state">{t('dealWholesalerTab.state')}</Label>
                    <Input
                      type="text"
                      name="state"
                      id="state"
                      value={formData.state}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterState')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="zip">{t('dealWholesalerTab.zip')}</Label>
                    <Input
                      type="text"
                      name="zip"
                      id="zip"
                      value={formData.zip}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterZip')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="4">
                  <FormGroup>
                    <Label for="county">{t('dealWholesalerTab.county')}</Label>
                    <Input
                      type="text"
                      name="county"
                      id="county"
                      value={formData.county}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterCounty')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              {/* Contact Information */}
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="contact_person">{t('dealWholesalerTab.contactPerson')}</Label>
                    <Input
                      type="text"
                      name="contact_person"
                      id="contact_person"
                      value={formData.contact_person}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterContactPerson')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="phone_1">{t('dealWholesalerTab.phone1')}</Label>
                    <Input
                      type="text"
                      name="phone_1"
                      id="phone_1"
                      value={formData.phone_1}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterPhone1')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="phone_2">{t('dealWholesalerTab.phone2')}</Label>
                    <Input
                      type="text"
                      name="phone_2"
                      id="phone_2"
                      value={formData.phone_2}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterPhone2')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="fax_1">{t('dealWholesalerTab.fax1')}</Label>
                    <Input
                      type="text"
                      name="fax_1"
                      id="fax_1"
                      value={formData.fax_1}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterFax1')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="fax_2">{t('dealWholesalerTab.fax2')}</Label>
                    <Input
                      type="text"
                      name="fax_2"
                      id="fax_2"
                      value={formData.fax_2}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterFax2')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label for="website">{t('dealWholesalerTab.website')}</Label>
                    <Input
                      type="text"
                      name="website"
                      id="website"
                      value={formData.website}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterWebsite')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>

              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label for="email">{t('dealWholesalerTab.email')}</Label>
                    <Input
                      type="email"
                      name="email"
                      id="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t('dealWholesalerTab.enterEmail')}
                      className="form-control"
                      style={inputStyle}
                      disabled
                    />
                  </FormGroup>
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
}
);

DealWholesalerTab.displayName = 'DealWholesalerTab';

export default DealWholesalerTab;